import { Spinner } from "@fluentui/react-components";
import * as React from "react";
import { useMemo } from "react";
import { useNotificationWebSocket } from "../../../context/NotificationWebSocketContext";
import { useApiResource } from "../../../hooks/useApiResource";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../store/StoreHooks";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { NotificationItem } from "../types";
import NotificationsContent from "./NotificationsContent";
import NotificationsHeader from "./NotificationsHeader";
import { usePanelStyles } from "./styles";

const notificationBaseUrl = `${process.env.REACT_APP_API_NOTIFICATION}?dismiss=FALSE`;

const NotificationsPanel: React.FC = () => {
  const styles = usePanelStyles();
  const airport = useAppSelector((state) => getAirportCode(state));
  const { notifications: socketNotifications } = useNotificationWebSocket();

  const url =
    notificationBaseUrl?.replace(AIRPORT_CODE_PARAMETER, airport) || "";
  const { data, showLoader, isError, refetch } = useApiResource({
    url: url,
    queryKey: [airport],
    refetchOnWindowFocus: false,
  });

  const mergedNotifications = useMemo(() => {
    const apiNotifications = (data?.data ?? []) as NotificationItem[];
    const notificationsById = new Map<string, NotificationItem>();

    apiNotifications.forEach((notification) => {
      if (!notification.dismiss) {
        notificationsById.set(notification.id, notification);
      }
    });

    socketNotifications.forEach((notification) => {
      if (notification.dismiss) {
        notificationsById.delete(notification.id);
      } else {
        notificationsById.set(notification.id, notification);
      }
    });

    return Array.from(notificationsById.values());
  }, [data, socketNotifications]);

  const noDataCard = (
    <NoDataCard message={EMPTY_STATE_MESSAGE.Notifications} marginAll={true} />
  );

  return (
    <section className={styles.panel}>
      <NotificationsHeader notificationCount={mergedNotifications.length} />

      {showLoader ? (
        <Spinner size="large" style={{ margin: "auto" }} />
      ) : isError ? (
        noDataCard
      ) : (
        <NotificationsContent
          airport={airport}
          notifications={mergedNotifications}
          refreshNotifications={refetch}
        />
      )}
    </section>
  );
};

export default NotificationsPanel;
